import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Banknote, Check, Loader2, RefreshCcw, Trash2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Bank account panel for creators.
 * - Loads Nigerian bank list from Paystack (via /api/banks)
 * - Two-step verification: pick bank + type 10-digit NUBAN → auto-resolves account name
 * - Save creates a Paystack transferrecipient server-side and stores the recipient_code
 * - Read-only display once linked; "Change" resets the form
 */
export default function BankAccountForm({ current, onSaved }) {
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [acctNum, setAcctNum] = useState("");
  const [acctName, setAcctName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!current);

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
  }, []);

  const canResolve = useMemo(
    () => bankCode && acctNum.length === 10 && !resolving,
    [bankCode, acctNum, resolving],
  );

  useEffect(() => {
    setResolved(false);
    setAcctName("");
  }, [bankCode, acctNum]);

  const doResolve = async () => {
    if (!canResolve) return;
    setResolving(true);
    try {
      const { data } = await api.post("/creator/bank-account/resolve", {
        bank_code: bankCode,
        account_number: acctNum,
      });
      if (data?.account_name) {
        setAcctName(data.account_name);
        setResolved(true);
      } else {
        toast.error("Couldn't confirm that account. Check the number and try again.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't confirm that account.");
    } finally {
      setResolving(false);
    }
  };

  const doSave = async () => {
    if (!resolved) return;
    setSaving(true);
    try {
      await api.post("/creator/bank-account", {
        bank_code: bankCode,
        bank_name: bankName,
        account_number: acctNum,
        account_name: acctName,
      });
      toast.success("Bank account linked. Future earnings will land here.");
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Unlink this bank account? You'll need to link one again before your next payout.")) return;
    try {
      await api.delete("/creator/bank-account");
      toast.success("Bank account removed.");
      setEditing(true);
      setBankCode(""); setBankName(""); setAcctNum(""); setAcctName(""); setResolved(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove.");
    }
  };

  if (current && !editing) {
    return (
      <div className="card-surface p-6" data-testid="bank-account-panel">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center shrink-0">
            <Check className="h-5 w-5 text-[#22C55E]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-eyebrow text-[#22C55E]">Payout account linked</p>
            <p className="font-display text-lg font-bold mt-1 truncate">{current.account_name}</p>
            <p className="text-sm text-white/60 mt-0.5">
              {current.bank_name} · <span className="tabular-nums">{current.account_number_masked}</span>
            </p>
            <p className="text-xs text-white/30 mt-2">
              Verified via Paystack{current.updated_at ? ` · updated ${new Date(current.updated_at).toLocaleDateString()}` : ""}
            </p>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button onClick={() => setEditing(true)} className="ghost-btn !py-2 !px-3 text-xs" data-testid="bank-change-button">
              <RefreshCcw className="h-3.5 w-3.5" /> Change
            </button>
            <button onClick={doDelete} className="ghost-btn !py-2 !px-3 text-xs hover:border-red-500/50 hover:text-red-400" data-testid="bank-delete-button">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="bank-account-form">
      <div className="flex items-start gap-4 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#E65F2B]/10 border border-[#E65F2B]/30 flex items-center justify-center shrink-0">
          <Banknote className="h-5 w-5 text-[#E65F2B]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-eyebrow">Payout account</p>
          <h3 className="font-display text-xl font-bold mt-1">Link your bank</h3>
          <p className="text-xs text-white/40 mt-1">
            NUBAN only (Nigerian banks). Paystack verifies the account before we save.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-eyebrow mb-2 block">Bank</label>
          <select
            value={bankCode}
            onChange={(e) => {
              const code = e.target.value;
              setBankCode(code);
              const bank = banks.find((b) => b.code === code);
              setBankName(bank ? bank.name : "");
            }}
            className="input-dark"
            disabled={banks.length === 0 || saving}
            data-testid="bank-select"
          >
            <option value="">{banks.length === 0 ? "Loading banks…" : "Select your bank…"}</option>
            {banks.map((b, idx) => (
              <option key={`${b.code}-${idx}`} value={b.code}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-eyebrow mb-2 block">10-digit account number</label>
          <div className="flex gap-2">
            <input
              value={acctNum}
              onChange={(e) => setAcctNum(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="0123456789"
              className="input-dark flex-1 tabular-nums"
              disabled={saving}
              data-testid="bank-account-number"
              inputMode="numeric"
            />
            <button
              type="button"
              onClick={doResolve}
              disabled={!canResolve || saving}
              className="ghost-btn whitespace-nowrap disabled:opacity-40"
              data-testid="bank-resolve-button"
            >
              {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </button>
          </div>
        </div>

        {resolved && (
          <div className="p-3 rounded-lg bg-[#22C55E]/5 border border-[#22C55E]/25 flex items-start gap-2" data-testid="bank-resolved-name">
            <Check className="h-4 w-4 text-[#22C55E] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-eyebrow text-[#22C55E]">Verified name</p>
              <p className="font-display font-bold truncate">{acctName}</p>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={doSave}
            disabled={!resolved || saving}
            className="ember-btn disabled:opacity-40"
            data-testid="bank-save-button"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link bank account"}
          </button>
          {current && (
            <button onClick={() => setEditing(false)} className="ghost-btn" data-testid="bank-cancel-button">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
